import { triggerToast } from '@components/base/Notification';
import { getRuntimeConfigSync } from '@utils/runtimeConfig';
import { first, map } from 'lodash';
import formatString from '@utils/stringHelper';
import { paymentOrderStatus, paymentStepRouteMap, paymentTransactionStatus } from '@containers/App/app.constant';
import { BOOK_PAY_CONFIRM_PAYLOAD, PREV_ORDER_ID, RBS_IN_QUERY, RESCHEDULED_ORDER_ID, localStorageGetItem, } from '@utils/localStorageHelpers';
export const extractPaymentDetailsData = (paymentResponse) => {
    if (!paymentResponse) {
        return [];
    }
    // console.log(paymentResponse, '::paymentResponse');
    const paymentId = paymentResponse?.id;
    const orderStatus = paymentResponse?.status;
    const orderItems = map(paymentResponse?.order_items, ({ item_id, order_id, unit_price, quantity, total_price, item: { status: item_status, drive_occurrence_guest: { id: drive_occurrence_guest_id, instruction, reschedule_instruction, cancel_instruction, drive_schedule: { id: drive_schedule_id, name: drive_schedule_name, start_date: drive_schedule_start_date, end_date: drive_schedule_end_date, slot_duration: drive_schedule_slot_duration, status: drive_schedule_status, slot_capacity: drive_schedule_slot_capacity, }, drive_occurrence = {}, }, sku, eligible_refund_rule, eligible_reschedule_rule, }, }) => {
        const { drive_schedule_id: drive_occurrence_id = null, start_date: drive_occurrence_start_date = null, end_date: drive_occurrence_end_date = null, status: drive_occurrence_status = null, workflow_step_id: drive_occurrence_workflow_step_id = null, } = drive_occurrence || {};
        return {
            item_id,
            order_id,
            quantity,
            unit_price,
            total_price,
            item_status,
            eligible_refund_rule,
            eligible_reschedule_rule,
            drive_occurrence_guest_id,
            instruction,
            reschedule_instruction,
            cancel_instruction,
            drive_schedule_id,
            drive_schedule_name,
            drive_schedule_start_date,
            drive_schedule_end_date,
            drive_schedule_slot_duration,
            drive_schedule_status,
            drive_schedule_slot_capacity,
            drive_occurrence_id,
            drive_occurrence_start_date,
            drive_occurrence_end_date,
            drive_occurrence_status,
            drive_occurrence_workflow_step_id,
            sku_name: sku?.name,
            sku_currency_code: sku?.currency_code,
            plan_type: sku?.plan_type,
            refund_rules: sku?.refund_rules,
            reschedule_rules: sku?.reschedule_rules,
        };
    });
    const transactionDetails = paymentResponse?.transactions.map(({ payment_id, external_id, status, total_amount, type, invoices, currency_code }) => ({
        transactionCurrencyCode: currency_code,
        transactionId: payment_id,
        transactionExternalId: external_id,
        transactionStatus: status,
        transactionTotalAmount: `${total_amount}`,
        transactionType: type,
        transactionInvoices: invoices && invoices.length > 0 ? [invoices[0]] : [],
    }));
    const formattedOrderDataArray = orderItems.map((orderItem) => {
        const firstTransactionDetail = transactionDetails?.length > 0 ? transactionDetails[0] : null;
        return {
            ...orderItem,
            payment_id: paymentId,
            order_status: orderStatus,
            transactionDetails: firstTransactionDetail,
        };
    });
    return formattedOrderDataArray;
};
export const formatCouponData = (data) => {
    const { order_id, coupon_code, order_items } = data;
    const firstOrderItem = first(order_items);
    const { coupon_id: item_couponId = null, id: item_id = null, item_id: item_itemId = null, order_item_id: item_orderItemId = null, unit_price: item_unitPrice = null, quantity: item_quantity = null, total_price: item_totalPrice = null, } = firstOrderItem || {};
    return {
        order_id,
        coupon_code,
        item_couponId,
        item_id,
        item_itemId,
        item_orderId: order_id,
        item_orderItemId,
        item_unitPrice,
        item_quantity,
        item_totalPrice,
        isValid: true,
    };
};
export const redirectToCXSlotBooking = ({ queryParams, tokens }) => {
    if (!queryParams)
        return;
    const { driveScheduleId, tid } = queryParams;
    const { accessToken } = tokens || {};
    let oldCandidateXUrl;
    try {
        const config = getRuntimeConfigSync();
        oldCandidateXUrl = config.OLD_CANDIDATE_X_URL;
    }
    catch {
        console.warn('[payment.helper] Runtime config not available');
    }
    if (!oldCandidateXUrl) {
        triggerToast({
            message: {
                title: 'Configuration Error',
                summary: 'Unable to redirect to slot booking. Please contact support.',
            },
            variant: 'danger',
        });
        return;
    }
    const url = new URL(`${oldCandidateXUrl}/scheduling/slot-booking`);
    url.searchParams.set('driveScheduleId', String(driveScheduleId));
    url.searchParams.set('tid', String(tid));
    if (accessToken) {
        url.searchParams.set('idToken', accessToken);
    }
    window.open(url.toString(), '_self');
};
export const redirectToInvoice = (invoiceNumber) => {
    const validateInvoice = () => {
        if (!invoiceNumber) {
            triggerToast({
                message: { title: 'Invoice not found', summary: 'We couldn’t find an invoice for this order.' },
                variant: 'danger',
            });
        }
        else {
            openInvoiceTab(`/payment/invoice?invoice_number=${invoiceNumber}`);
        }
    };
    validateInvoice();
};
const openInvoiceTab = (invoiceUrl, isOpenInNewTab = true) => {
    if (isOpenInNewTab) {
        window.open(invoiceUrl, '_blank', 'noopener');
    }
    else {
        window.open(invoiceUrl, '_parent');
    }
};
export const roundToTwoDecimals = (value, fractionDigits = 2) => {
    try {
        if (value === null || value === undefined || isNaN(value)) {
            return '0.00';
        }
        const rounded = Math.round(value * 100) / 100;
        return rounded.toFixed(fractionDigits);
    }
    catch {
        return '0.00';
    }
};
export function mapTransactionData(apiData) {
    if (!apiData)
        return null;
    return {
        transactionCurrencyCode: apiData.currency_code,
        transactionId: apiData.payment_id,
        transactionExternalId: apiData.external_id,
        transactionStatus: apiData.status,
        transactionTotalAmount: apiData.total_amount,
        transactionInvoices: apiData.invoices,
    };
}
export const resolvePaymentRouteSegment = (order) => {
    const orderStatus = formatString(order?.order_status ?? '', true);
    const transactionStatus = formatString(order?.transactionDetails?.transactionStatus ?? '', true);
    switch (orderStatus) {
        case paymentOrderStatus.COMPLETED: {
            return paymentStepRouteMap.PAYMENT;
        }
        case paymentOrderStatus.PAYMENT_PENDING: {
            if (transactionStatus === paymentTransactionStatus.PENDING ||
                transactionStatus === paymentTransactionStatus.INITIATED) {
                return paymentStepRouteMap.PAYMENT_PENDING;
            }
            return paymentStepRouteMap.PAYMENT;
        }
        case paymentOrderStatus.DRAFT: {
            return paymentStepRouteMap.PAYMENT;
        }
        default:
            return paymentStepRouteMap.PAYMENT;
    }
};
export const getCurrentSegment = (location, path) => {
    try {
        const pathParts = location.pathname.split('/');
        const paymentIndex = pathParts.indexOf(path);
        return paymentIndex >= 0 ? (pathParts[paymentIndex + 1] ?? '') : '';
    }
    catch {
        return null;
    }
};
export const loadStoredBookPayPayload = () => {
    try {
        const raw = localStorageGetItem(BOOK_PAY_CONFIRM_PAYLOAD);
        return raw ? JSON.parse(raw) : null;
    }
    catch {
        return null;
    }
};
export const shouldSkipFlow = () => {
    try {
        const rbsFlag = localStorageGetItem(RBS_IN_QUERY);
        if (!rbsFlag) {
            return false;
        }
        const prevOrderId = localStorageGetItem(PREV_ORDER_ID);
        const rescheduledOrderId = localStorageGetItem(RESCHEDULED_ORDER_ID);
        return Boolean(prevOrderId && rescheduledOrderId && prevOrderId === rescheduledOrderId);
    }
    catch {
        return false;
    }
};
